import type { GetPayV3Query, PayV3Overtime } from '@revelio/data-access';

import type { CompensationChartType } from '../../PageConfigProvider/types/chart';
import type { PayMetricValues } from '../../PageConfigProvider/types/metric';
import {
  type BreakdownData,
  type BreakdownSingleDataSet,
  DataType,
  type SingleDataPoint,
  type SingleDataSet,
} from '../types';

type MultiCompensationData = {
  dim: string;
  data: GetPayV3Query['pay_v3'] | undefined;
};

const getOvertimeMetricData = ({
  data,
  metric,
}: {
  data: PayV3Overtime;
  metric: PayMetricValues;
}): number[] => {
  switch (metric) {
    case 'base_salary':
    case 'base_salary_percentiles':
      return data?.salary_base || [];
    case 'total_compensation':
    case 'total_compensation_percentiles':
      return data?.salary_total || [];
    case 'contractor_pay_rate':
    case 'contractor_pay_rate_percentiles':
      return data?.salary_hourly || [];
    case 'total_spend_salary':
      return data?.salary_base || [];
    case 'total_spend_compensation':
      return data?.salary_total || [];
    default:
      return [];
  }
};

export const convertCompensationTopNData = ({
  data,
  metric,
  chartType,
  exchangeRate = 1,
}: {
  data: MultiCompensationData[];
  metric: PayMetricValues;
  chartType: CompensationChartType;
  exchangeRate?: number;
}): SingleDataSet | BreakdownSingleDataSet => {
  if (chartType === 'grouped-bar' || chartType === 'stacked-bar') {
    const groupedData: BreakdownSingleDataSet['data'] =
      data.map((primaryEntity): BreakdownData<SingleDataPoint> => {
        const breakdown: SingleDataPoint[] =
          primaryEntity?.data?.entities.map((subEntity): SingleDataPoint => {
            const overtimeData = subEntity.metrics?.overtime;
            const metricDataValues = getOvertimeMetricData({
              data: overtimeData,
              metric,
            });

            const lastTimeseriesValue =
              metricDataValues?.[metricDataValues.length - 1];
            const convertedValue =
              typeof lastTimeseriesValue === 'number'
                ? lastTimeseriesValue * exchangeRate
                : null;

            return {
              dim: subEntity.short_name,
              value: convertedValue,
              share: convertedValue,
            };
          }) ?? [];

        return {
          dim: primaryEntity.dim,
          breakdown,
        };
      }) ?? [];

    return { type: DataType.BREAKDOWN_SINGLE, data: groupedData };
  }

  const barData: SingleDataSet['data'] =
    data?.[0]?.data?.entities
      ?.map((entity) => {
        const overtimeData = entity.metrics?.overtime;
        const metricDataValues = getOvertimeMetricData({
          data: overtimeData,
          metric,
        });
        const lastTimeseriesValue =
          metricDataValues?.[metricDataValues.length - 1];
        const convertedValue =
          typeof lastTimeseriesValue === 'number'
            ? lastTimeseriesValue * exchangeRate
            : null;

        return {
          dim: entity.short_name,
          value: convertedValue,
        };
      })
      ?.sort((a, b) => (b.value ?? 0) - (a.value ?? 0)) ?? [];

  return { type: DataType.SINGLE, data: barData };
};
